import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { 
  Heart, 
  Users, 
  Award, 
  Sparkles, 
  Star, 
  Shield,
  Zap,
  Quote,
  ChevronDown,
  ArrowRight,
  Images
} from "lucide-react";
import axios from "axios";
import { trackViewContent } from "../utils/metaTracking";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Helper to get full image URL
const getImageUrl = (url) => {
  if (!url) return "";
  if (url.startsWith("http")) return url;
  if (url.startsWith("/uploads")) {
    return `${process.env.REACT_APP_BACKEND_URL}${url}`;
  }
  return url;
};

const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.15 }
  }
};

// Luxury Hero Section
const HeroSection = ({ content, styles }) => (
  <motion.section
    initial="hidden"
    animate="visible"
    variants={staggerContainer}
    className="relative min-h-screen flex items-center justify-center overflow-hidden"
    style={{
      paddingTop: `${styles?.padding_top || 80}px`,
      paddingBottom: `${styles?.padding_bottom || 80}px`,
    }}
  >
    {content.background_image && (
      <>
        <div 
          className="absolute inset-0 bg-cover bg-center scale-105"
          style={{ 
            backgroundImage: `url(${getImageUrl(content.background_image)})`,
            animation: 'slowZoom 20s ease-in-out infinite alternate'
          }}
        />
        <div 
          className="absolute inset-0"
          style={{ 
            background: `linear-gradient(to bottom, rgba(5,5,5,${(content.overlay_opacity || 55) / 100}) 0%, rgba(5,5,5,0.8) 100%)`
          }}
        />
      </>
    )}
    
    {/* Decorative elements */}
    <div className="absolute top-20 left-10 w-32 h-32 border border-[#D4AF37]/20 rotate-45 opacity-30" />
    <div className="absolute bottom-20 right-10 w-24 h-24 border border-[#D4AF37]/20 rotate-12 opacity-30" />
    
    <div className="relative z-10 max-w-5xl mx-auto px-6 text-center">
      {/* Decorative line */}
      <motion.div 
        variants={fadeInUp}
        className="flex items-center justify-center gap-4 mb-8"
      >
        <div className="w-16 h-px bg-gradient-to-r from-transparent to-[#D4AF37]" />
        <Sparkles className="w-5 h-5 text-[#D4AF37]" />
        <div className="w-16 h-px bg-gradient-to-l from-transparent to-[#D4AF37]" />
      </motion.div>
      
      <motion.h1 
        variants={fadeInUp}
        className="text-5xl md:text-6xl lg:text-7xl font-['Playfair_Display'] font-bold text-[#F5F5F0] mb-6 leading-tight"
      >
        {content.heading}
      </motion.h1>
      
      {content.subheading && (
        <motion.p 
          variants={fadeInUp}
          className="text-xl md:text-2xl text-[#D4AF37] mb-6 font-light tracking-wide"
        >
          {content.subheading}
        </motion.p>
      )}
      
      {content.text && (
        <motion.div 
          variants={fadeInUp}
          className="text-lg md:text-xl text-[#A1A1AA] max-w-3xl mx-auto leading-relaxed whitespace-pre-line mb-10"
        >
          {content.text}
        </motion.div>
      )}
      
      {content.button_text && (
        <motion.div variants={fadeInUp}>
          <Link
            to={content.button_link || '/booking'}
            className="group inline-flex items-center gap-3 px-10 py-4 bg-[#D4AF37] text-[#050505] font-semibold text-lg transition-all duration-500 hover:bg-[#F5F5F0] hover:scale-105 hover:shadow-[0_0_40px_rgba(212,175,55,0.3)]"
          >
            {content.button_text}
            <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1" />
          </Link>
        </motion.div>
      )}
      
      {/* Scroll indicator */}
      <motion.div 
        variants={fadeInUp}
        className="absolute bottom-10 left-1/2 -translate-x-1/2"
      >
        <div className="flex flex-col items-center gap-2 text-[#D4AF37]/50 animate-bounce">
          <span className="text-xs uppercase tracking-widest">Scroll</span>
          <ChevronDown className="w-5 h-5" />
        </div>
      </motion.div>
    </div>
  </motion.section>
);

// Text Section
const TextSection = ({ content, styles }) => (
  <motion.section
    initial="hidden"
    whileInView="visible"
    viewport={{ once: true, margin: "-100px" }}
    variants={staggerContainer}
    className="px-6"
    style={{
      paddingTop: `${styles?.padding_top || 60}px`,
      paddingBottom: `${styles?.padding_bottom || 60}px`,
      backgroundColor: styles?.background_color || "transparent",
    }}
  >
    <div className="max-w-4xl mx-auto">
      {content.title && (
        <motion.h2 
          variants={fadeInUp}
          className="text-3xl md:text-5xl font-['Playfair_Display'] font-bold text-[#F5F5F0] mb-4 text-center"
        >
          {content.title}
        </motion.h2>
      )}
      {content.subtitle && (
        <motion.p 
          variants={fadeInUp}
          className="text-xl text-[#D4AF37] mb-8 text-center font-light tracking-wide"
        >
          {content.subtitle}
        </motion.p>
      )}
      {content.text && (
        <motion.div 
          variants={fadeInUp}
          className="prose prose-lg prose-invert max-w-none text-[#A1A1AA] [&_p]:mb-4 [&_strong]:text-[#D4AF37] [&_em]:text-[#F5F5F0]"
          style={{ textAlign: content.align || 'center' }}
          dangerouslySetInnerHTML={{ __html: content.text }}
        />
      )}
    </div>
  </motion.section>
);

// Stats Section
const StatsSection = ({ content, styles }) => {
  const stats = content.stats || [];
  
  return (
    <motion.section
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true }}
      variants={staggerContainer}
      className="px-6 relative overflow-hidden"
      style={{
        paddingTop: `${styles?.padding_top || 60}px`,
        paddingBottom: `${styles?.padding_bottom || 60}px`,
        backgroundColor: styles?.background_color || "#0A0A0A",
      }}
    >
      {/* Decorative background */}
      <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxwYXRoIGQ9Ik0zNiAxOGMtOS45NDEgMC0xOCA4LjA1OS0xOCAxOHM4LjA1OSAxOCAxOCAxOCAxOC04LjA1OSAxOC0xOC04LjA1OS0xOC0xOC0xOHptMCAzMmMtNy43MzIgMC0xNC02LjI2OC0xNC0xNHM2LjI2OC0xNCAxNC0xNCAxNCA2LjI2OCAxNCAxNC02LjI2OCAxNC0xNCAxNHoiIGZpbGw9IiNEMUFGMzciIGZpbGwtb3BhY2l0eT0iLjAzIi8+PC9nPjwvc3ZnPg==')] opacity-50" />
      
      <div className="max-w-6xl mx-auto relative">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
          {stats.map((stat, i) => (
            <motion.div 
              key={i} 
              variants={fadeInUp}
              className="text-center group"
            >
              <div className="relative inline-block">
                <div className="text-5xl md:text-6xl font-bold text-[#D4AF37] mb-2 font-['Playfair_Display']">
                  {stat.value}{stat.suffix}
                </div>
                <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-12 h-0.5 bg-[#D4AF37]/30 group-hover:w-full transition-all duration-500" />
              </div>
              <p className="text-[#A1A1AA] uppercase tracking-widest text-sm mt-4">{stat.label}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </motion.section>
  );
};

// Features Section
const FeaturesSection = ({ content, styles }) => {
  const features = content.features || [];
  const cols = content.columns || 4;
  
  const iconMap = {
    heart: Heart,
    star: Star,
    shield: Shield,
    zap: Zap,
    users: Users,
    award: Award,
    sparkles: Sparkles
  };

  return (
    <motion.section
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, margin: "-100px" }}
      variants={staggerContainer}
      className="px-6"
      style={{
        paddingTop: `${styles?.padding_top || 40}px`,
        paddingBottom: `${styles?.padding_bottom || 40}px`,
        backgroundColor: styles?.background_color || "transparent",
      }}
    >
      <div className="max-w-6xl mx-auto">
        {content.title && (
          <motion.h2 
            variants={fadeInUp}
            className="text-3xl md:text-4xl font-['Playfair_Display'] font-bold text-[#F5F5F0] mb-12 text-center"
          >
            {content.title}
          </motion.h2>
        )}
        <div 
          className="grid gap-6"
          style={{ gridTemplateColumns: `repeat(${Math.min(cols, 4)}, 1fr)` }}
        >
          {features.map((feature, i) => {
            const IconComp = iconMap[feature.icon] || Sparkles;
            return (
              <motion.div 
                key={i} 
                variants={fadeInUp}
                className="text-center p-8 border border-[rgba(212,175,55,0.1)] hover:border-[rgba(212,175,55,0.4)] transition-all duration-500 group bg-gradient-to-b from-transparent to-[rgba(212,175,55,0.02)] hover:to-[rgba(212,175,55,0.05)]"
              >
                <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-[#D4AF37]/10 flex items-center justify-center group-hover:bg-[#D4AF37]/20 transition-all duration-500 group-hover:scale-110">
                  <IconComp className="w-9 h-9 text-[#D4AF37]" />
                </div>
                <h3 className="text-xl font-['Playfair_Display'] font-semibold text-[#F5F5F0] mb-3">
                  {feature.title}
                </h3>
                <p className="text-[#A1A1AA] leading-relaxed">{feature.description}</p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </motion.section>
  );
};

// Divider Section
const DividerSection = ({ content }) => (
  <div className="flex justify-center py-8">
    {content.style === 'ornament' ? (
      <div className="flex items-center gap-6">
        <div className="w-20 h-px bg-gradient-to-r from-transparent to-[#D4AF37]" />
        <div className="w-3 h-3 bg-[#D4AF37] transform rotate-45" />
        <div className="w-20 h-px bg-gradient-to-l from-transparent to-[#D4AF37]" />
      </div>
    ) : (
      <div 
        style={{ 
          width: content.width || '50%',
          height: `${content.thickness || 1}px`,
          backgroundColor: content.color || '#D4AF37',
        }}
      />
    )}
  </div>
);

// Two Column Section
const TwoColumnSection = ({ content, styles }) => (
  <motion.section
    initial="hidden"
    whileInView="visible"
    viewport={{ once: true, margin: "-100px" }}
    variants={staggerContainer}
    style={{
      paddingTop: `${styles?.padding_top || 60}px`,
      paddingBottom: `${styles?.padding_bottom || 60}px`,
      backgroundColor: styles?.background_color || "transparent",
    }}
  >
    <div className="max-w-7xl mx-auto">
      <div className={`grid md:grid-cols-2 ${content.reverseOnMobile ? 'md:grid-flow-dense' : ''}`}>
        <motion.div 
          variants={fadeInUp}
          className="[&_h2]:text-[#F5F5F0] [&_h2]:font-['Playfair_Display'] [&_h3]:text-[#F5F5F0] [&_strong]:text-[#D4AF37] [&_p]:text-[#A1A1AA] [&_a]:inline-block [&_a]:transition-all [&_a]:duration-300 [&_a:hover]:bg-[#B8962E] [&_a:hover]:scale-105"
          dangerouslySetInnerHTML={{ __html: content.leftContent || '' }}
        />
        <motion.div 
          variants={fadeInUp}
          className={`[&_h2]:text-[#F5F5F0] [&_h3]:text-[#F5F5F0] ${content.reverseOnMobile ? 'md:col-start-1 md:row-start-1' : ''}`}
          dangerouslySetInnerHTML={{ __html: content.rightContent || '' }}
        />
      </div>
    </div>
  </motion.section>
);

// Gallery Section
const GallerySection = ({ content, styles }) => {
  const images = content.images || [];
  const cols = content.columns || 4;
  
  return (
    <motion.section
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, margin: "-100px" }}
      variants={staggerContainer}
      className="px-6"
      style={{
        paddingTop: `${styles?.padding_top || 40}px`,
        paddingBottom: `${styles?.padding_bottom || 40}px`,
        backgroundColor: styles?.background_color || "transparent",
      }}
    >
      <div className="max-w-6xl mx-auto">
        {content.title && (
          <motion.h2 
            variants={fadeInUp}
            className="text-3xl md:text-4xl font-['Playfair_Display'] font-bold text-[#F5F5F0] mb-12 text-center"
          >
            {content.title}
          </motion.h2>
        )}
        <div 
          className="grid gap-4"
          style={{ gridTemplateColumns: `repeat(${cols}, 1fr)` }}
        >
          {images.map((img, i) => (
            <motion.div 
              key={i} 
              variants={fadeInUp}
              className="relative group overflow-hidden aspect-square"
            >
              <img 
                src={getImageUrl(img.url)} 
                alt={img.caption || ''} 
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              {img.caption && (
                <div className="absolute bottom-0 left-0 right-0 p-4 translate-y-full group-hover:translate-y-0 transition-transform duration-500">
                  <p className="text-[#F5F5F0] font-medium">{img.caption}</p>
                </div>
              )}
            </motion.div>
          ))}
        </div>
      </div>
    </motion.section>
  );
};

// CTA Section
const CTASection = ({ content, styles }) => (
  <motion.section
    initial="hidden"
    whileInView="visible"
    viewport={{ once: true }}
    variants={fadeInUp}
    className="px-6"
    style={{
      paddingTop: `${styles?.padding_top || 40}px`,
      paddingBottom: `${styles?.padding_bottom || 40}px`,
      backgroundColor: styles?.background_color || "transparent",
    }}
  >
    <div className="max-w-3xl mx-auto text-center">
      {content.heading && (
        <h2 className="text-3xl md:text-4xl font-['Playfair_Display'] font-bold text-[#F5F5F0] mb-4">
          {content.heading}
        </h2>
      )}
      {content.subtext && (
        <p className="text-xl text-[#A1A1AA] mb-8">{content.subtext}</p>
      )}
      {content.buttonText && (
        <Link
          to={content.buttonLink || '/contact'}
          className={`inline-flex items-center gap-2 px-10 py-4 font-semibold text-lg transition-all duration-300 ${
            content.buttonStyle === 'outline' 
              ? 'border-2 border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-[#050505]'
              : 'bg-[#D4AF37] text-[#050505] hover:bg-[#B8962E] hover:scale-105'
          }`}
        >
          {content.buttonText}
          <ArrowRight className="w-5 h-5" />
        </Link>
      )}
    </div>
  </motion.section>
);

// Testimonials Grid Section
const TestimonialsGridSection = ({ content, styles }) => {
  const testimonials = content.testimonials || [];
  const cols = content.columns || 3;

  return (
    <motion.section
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, margin: "-100px" }}
      variants={staggerContainer}
      className="px-6"
      style={{
        paddingTop: `${styles?.padding_top || 60}px`,
        paddingBottom: `${styles?.padding_bottom || 60}px`,
        backgroundColor: styles?.background_color || "#0A0A0A",
      }}
    >
      <div className="max-w-7xl mx-auto">
        {content.title && (
          <motion.div variants={fadeInUp} className="text-center mb-16">
            <div className="flex items-center justify-center gap-4 mb-6">
              <div className="w-12 h-px bg-[#D4AF37]" />
              <Quote className="w-6 h-6 text-[#D4AF37]" />
              <div className="w-12 h-px bg-[#D4AF37]" />
            </div>
            <h2 className="text-3xl md:text-5xl font-['Playfair_Display'] font-bold text-[#F5F5F0]">
              {content.title}
            </h2>
          </motion.div>
        )}
        <div 
          className="grid gap-8"
          style={{ gridTemplateColumns: `repeat(${cols}, 1fr)` }}
        >
          {testimonials.map((t, i) => (
            <motion.div 
              key={i} 
              variants={fadeInUp}
              className="p-8 border border-[rgba(212,175,55,0.15)] hover:border-[rgba(212,175,55,0.4)] transition-all duration-500 bg-gradient-to-b from-[rgba(212,175,55,0.03)] to-transparent group"
            >
              <div className="flex mb-4">
                {[...Array(5)].map((_, j) => (
                  <Star key={j} className="w-4 h-4 text-[#D4AF37] fill-current" />
                ))}
              </div>
              <p className="text-[#F5F5F0] italic mb-6 leading-relaxed text-lg">"{t.quote}"</p>
              <div className="flex items-center gap-4 pt-4 border-t border-[rgba(212,175,55,0.1)]">
                {t.authorImage ? (
                  <img 
                    src={getImageUrl(t.authorImage)} 
                    alt={t.authorName}
                    className="w-12 h-12 rounded-full object-cover border-2 border-[#D4AF37]/30"
                  />
                ) : (
                  <div className="w-12 h-12 rounded-full bg-[#D4AF37]/10 flex items-center justify-center border-2 border-[#D4AF37]/30">
                    <span className="text-[#D4AF37] font-semibold text-lg">{(t.authorName || "?")[0]}</span>
                  </div>
                )}
                <div>
                  <p className="font-semibold text-[#D4AF37]">{t.authorName}</p>
                  <p className="text-[#A1A1AA] text-sm">{t.authorTitle}</p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </motion.section>
  );
};

// Section Renderer Map
const SECTION_RENDERERS = {
  hero: HeroSection,
  text: TextSection,
  stats: StatsSection,
  features: FeaturesSection,
  divider: DividerSection,
  two_column: TwoColumnSection,
  gallery: GallerySection,
  cta: CTASection,
  testimonials_grid: TestimonialsGridSection,
};

const HomePage = () => {
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchPage = async () => {
      try {
        const res = await axios.get(`${API}/public/page/home`);
        setPage(res.data);
        document.title = "Mix & Mingle | The Room You've Been Looking For";
        // Meta: ViewContent for homepage
        trackViewContent({ slug: 'home', title: 'Mix & Mingle Homepage', businessLine: 'general' });
      } catch (err) {
        console.error("Error fetching home page:", err);
        setError("Failed to load page content");
      } finally {
        setLoading(false);
      }
    };
    
    fetchPage();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="relative w-16 h-16">
            <div className="absolute inset-0 border-2 border-[#D4AF37] rounded-full animate-ping opacity-20" />
            <div className="absolute inset-0 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin" />
          </div>
          <p className="text-[#D4AF37] text-sm uppercase tracking-widest">Loading</p>
        </div>
      </div>
    );
  }

  if (error || !page) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-['Playfair_Display'] text-[#F5F5F0] mb-4">Something went wrong</h1>
          <p className="text-[#A1A1AA] mb-8">We couldn't load the page content.</p>
          <button 
            onClick={() => window.location.reload()} 
            className="text-[#D4AF37] hover:underline"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505]">
      {/* Custom CSS for animations */}
      <style>{`
        @keyframes slowZoom {
          from { transform: scale(1); }
          to { transform: scale(1.1); }
        }
      `}</style>
      
      {page.sections?.map((section, index) => {
        const Renderer = SECTION_RENDERERS[section.type];
        if (!Renderer) {
          console.warn(`No renderer for section type: ${section.type}`);
          return null;
        }
        return (
          <Renderer 
            key={section.id || index} 
            content={section.content || {}} 
            styles={section.styles || {}}
          />
        );
      })}
    </div>
  );
};

export default HomePage;
